/**
 * BudgetItem Component
 *
 * 카테고리별 예산 대비 지출 현황을 표시하는 아이템 컴포넌트
 *
 * 확장 기능:
 * - 소분류 예산 현황 표시 (접기/펼치기)
 * - 기본 상태: 접힘
 * - 클릭 시 소분류 목록 토글
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BudgetMinorItem } from './BudgetMinorItem'
import type { BudgetStatus } from '../types/Budget'
import type { Category } from '@/domains/category/types/Category'

/**
 * BudgetItem Props
 */
export interface BudgetItemProps {
  /** 예산 상태 데이터 */
  budgetStatus: BudgetStatus
  /** 카테고리 정보 (실제 구글 시트 데이터) */
  category: Category | undefined
}

/**
 * 카테고리별 예산 현황 아이템
 *
 * 책임:
 * - 카테고리 아이콘 + 이름 표시
 * - 예산, 지출, 남은 금액 표시
 * - 사용 비율 프로그레스 바 (색상 코딩)
 * - 초과/남음 상태 표시
 *
 * 색상 규칙:
 * - < 60%: 초록색 (안전)
 * - 60-90%: 주황색 (주의)
 * - > 90%: 빨간색 (위험)
 * - 초과: 빨간색 + "초과 X원" 표시
 */
export function BudgetItem({ budgetStatus, category }: BudgetItemProps) {
  const { budget, spent, remaining, percentage, isOverBudget, minorBreakdown, hasNoBudget } = budgetStatus
  const navigate = useNavigate()

  /**
   * 접기/펼치기 상태
   * 기본값: false (접힘)
   */
  const [isExpanded, setIsExpanded] = useState(false)

  /**
   * 카테고리 정보
   * props로 받은 카테고리 사용 (실제 구글 시트 데이터)
   */
  const categoryIcon = category?.icon || '❓'
  const categoryName = category?.name || '알 수 없음'

  /**
   * 소분류 존재 여부 및 개수
   */
  const hasMinor = minorBreakdown && minorBreakdown.length > 0
  const minorsWithBudget = minorBreakdown?.filter(m => !m.hasNoBudget) || []
  const visibleCount = minorsWithBudget.length

  /**
   * 헤더 클릭 핸들러 (접기/펼치기 토글)
   * 소분류가 없으면 클릭해도 아무 동작 안 함
   */
  const handleHeaderClick = () => {
    if (hasMinor) {
      setIsExpanded(!isExpanded)
    }
  }

  /**
   * 사용 비율에 따른 색상 결정
   */
  const getColor = (percentage: number, isOverBudget: boolean) => {
    if (isOverBudget) return 'red'
    if (percentage < 60) return 'green'
    if (percentage < 90) return 'orange'
    return 'red'
  }

  const color = getColor(percentage, isOverBudget)

  /**
   * Tailwind CSS 색상 클래스 매핑
   * bg: 프로그레스 바 배경 (테마 색상 사용)
   * text, lightText: 텍스트 색상 (Tailwind 기본 색상 유지)
   */
  const colorClasses = {
    green: {
      bg: 'bg-budget-safe',
      text: 'text-green-700',
      lightText: 'text-green-600',
    },
    orange: {
      bg: 'bg-budget-warning',
      text: 'text-orange-700',
      lightText: 'text-orange-600',
    },
    red: {
      bg: 'bg-budget-danger',
      text: 'text-red-700',
      lightText: 'text-red-600',
    },
  }

  const currentColor = colorClasses[color]

  /**
   * 숫자를 천단위 구분자로 포맷
   */
  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('ko-KR')
  }

  /**
   * 상태 메시지 생성
   */
  const getStatusMessage = () => {
    if (isOverBudget) {
      return `초과 ${formatCurrency(Math.abs(remaining))}원`
    }
    return `남음 ${formatCurrency(remaining)}원`
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
      {/* Header: Category Info (클릭 가능) */}
      <div
        className={`flex items-center justify-between mb-3 ${hasMinor ? 'cursor-pointer' : ''}`}
        onClick={handleHeaderClick}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{categoryIcon}</span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{categoryName}</h3>
              {hasNoBudget && (
                <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium rounded">
                  예산 미설정
                </span>
              )}
              {hasMinor && !hasNoBudget && (
                <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                  <span>{isExpanded ? '▼' : '▶'}</span>
                  {visibleCount > 0 && <span>{visibleCount}개</span>}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {hasNoBudget ? '예산: 미설정' : `예산: ${formatCurrency(budget.amount)}원`}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${currentColor.text}`}>
            {percentage.toFixed(1)}%
          </div>
          <div className={`text-xs font-medium ${currentColor.lightText}`}>
            {getStatusMessage()}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-2">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className={`${currentColor.bg} h-2 rounded-full transition-all duration-500 ease-out`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Amount Info */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          사용: <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(spent)}원</span>
        </span>
        {!isOverBudget && !hasNoBudget && (
          <span className="text-gray-500 dark:text-gray-400">
            {formatCurrency(budget.amount)}원
          </span>
        )}
      </div>

      {/* 예산 미설정 안내 메시지 */}
      {hasNoBudget && (
        <div className="mt-3 bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
          <p className="text-sm text-red-800 dark:text-red-300 mb-2">
            이 카테고리에 예산이 설정되지 않았습니다.
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate('/settings/budget')
            }}
            className="text-xs text-red-600 dark:text-red-400 underline hover:text-red-700 dark:hover:text-red-300"
          >
            예산 설정하기 →
          </button>
        </div>
      )}

      {/* Minor Categories (접기/펼치기) */}
      {hasMinor && isExpanded && (
        <div className="mt-4 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-4">
          {minorBreakdown!.map(minorStatus => {
            const minorCategory = category?.children?.find(
              c => c.id === minorStatus.minorCategoryId
            )

            return (
              <BudgetMinorItem
                key={minorStatus.minorCategoryId}
                minorStatus={minorStatus}
                category={minorCategory}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
